/**
 * Automatic structured data for pages that don't pass their own.
 *
 * - Breadcrumbs are derived from the URL path (/research/grants →
 *   Home › Research › Grants) so every page is eligible for the
 *   breadcrumb trail in Google results.
 * - The WebPage subtype is picked from the path (AboutPage, ContactPage,
 *   CollectionPage, FAQPage-free) so search engines understand the page role.
 * - Key university pages get an extra entity (campuses, research, admissions…)
 *   linked back to the site-wide CollegeOrUniversity node.
 */
const SITE_URL = "https://www.zetech.ac.ke";
const ORG = { "@id": `${SITE_URL}/#organization` };

const LABELS = {
  "about-zu": "About ZU",
  faqs: "FAQs",
  kuccps: "KUCCPS",
  ias: "Institute of Ageing Studies",
  "our-campuses": "Our Campuses",
  "agritech-innovation-hub": "AgriTech Innovation Hub",
  "whistle-blowing": "Whistle Blowing",
  cpc: "CPC",
};

const titleCase = (seg) =>
  LABELS[seg] ||
  decodeURIComponent(seg)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function autoBreadcrumbs(pathname) {
  const segs = String(pathname || "/")
    .split(/[?#]/)[0]
    .split("/")
    .filter(Boolean);
  if (segs.length === 0) return null;
  const crumbs = [{ name: "Home", path: "/" }];
  let acc = "";
  segs.forEach((seg) => {
    acc += `/${seg}`;
    crumbs.push({ name: titleCase(seg), path: acc });
  });
  return crumbs;
}

const TYPE_RULES = [
  [/^\/(about-zu|mission-vision|our-foundation|charter|governance|zetech-anthem)$/, "AboutPage"],
  [/^\/(contact|contact-us|student-support)$/, "ContactPage"],
  [/^\/(programmes|blog|events|research|institutes|testimonials)(\/(school|level|qualification)\/[^/]+)?$/, "CollectionPage"],
];

export function autoWebPageType(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  const hit = TYPE_RULES.find(([rx]) => rx.test(p));
  return hit ? hit[1] : "WebPage";
}

const CAMPUSES = [
  { name: "Zetech University — Ruiru Campus", street: "Off Thika Road, Ruiru", locality: "Ruiru" },
  { name: "Zetech University — Mang'u Campus", street: "Thika–Mang'u Road", locality: "Thika" },
];

export function autoPageEntities(pathname, canonical) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  switch (p) {
    case "/our-campuses":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${canonical}#campuses`,
          name: "Zetech University campuses",
          itemListElement: CAMPUSES.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Place",
              name: c.name,
              containedInPlace: ORG,
              address: {
                "@type": "PostalAddress",
                streetAddress: c.street,
                addressLocality: c.locality,
                addressCountry: "KE",
              },
            },
          })),
        },
      ];
    case "/research":
    case "/research/projects":
    case "/research/patents":
    case "/research/consultancies":
    case "/research/grants":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ResearchProject",
          "@id": `${canonical}#research`,
          name: `Zetech University ${p === "/research" ? "Research & Innovation" : titleCase(p.split("/").pop())}`,
          url: canonical,
          parentOrganization: ORG,
        },
      ];
    case "/admissions":
    case "/application-process":
    case "/intakes":
      return [
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": `${canonical}#howto`,
          name: "How to apply to Zetech University",
          step: [
            { "@type": "HowToStep", position: 1, name: "Choose a programme", url: `${SITE_URL}/programmes` },
            { "@type": "HowToStep", position: 2, name: "Check the fees", url: `${SITE_URL}/tuition-fees` },
            { "@type": "HowToStep", position: 3, name: "Submit your application", url: `${SITE_URL}/application-process` },
          ],
        },
      ];
    default:
      return [];
  }
}
